// The Director in the editing suite: the orb with its latest line, the mic mute, and the chat in a sheet (phones)
// or a collapsible panel (desktop). The orb's state word is debounced, so a quick speaking/listening flip doesn't
// flicker, and nothing here changes size between states.
import { useEffect, useRef, useState } from "react";
import { callCredits, fmtClock, limitWord } from "../callmeter";
import { pauseCall } from "../director";
import { hangupLine } from "../callend";
import { isActive } from "../playback";
import { Link } from "../router";
import { set, useStore, type DirectorStatus } from "../store";
import { Icon, Sheet } from "./bits";
import { DirectorLight } from "./DirectorLight";
import { usePersona } from "./DirectorPicker";
import { Composer, isLive, toggleTalk, Transcript } from "./Talk";
import { typedWord } from "../typedchat";
import { CallBanner } from "../stage/CallBanner";
import { Fold } from "./Fold"; // v4 (V4-P2): the conversation folds like every other detail

/**
 * The Director's status light where the iris is too big: the user's Director character, a small film light that
 * glows with its voice (components/DirectorLight.tsx). It replaced the brand mark here (Key Light Director).
 */
export function DirectorMark({ status }: { status: DirectorStatus }) {
  const persona = usePersona();
  return <DirectorLight preset={persona.preset} status={status} size="100%" className="director-mark" />;
}

/**
 * Director calls are charged per minute (v3): beside the call button, the rate ("30 credits a minute"); during a
 * call, its running time and cost, and a heads-up in its last minute.
 */
export function CallMeter({ className = "" }: { className?: string }) {
  const call = useStore((s) => s.directorCall);
  const rate = useStore((s) => s.directorRate);
  const textRate = useStore((s) => s.directorTextRate);
  const status = useStore((s) => s.director);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!call) return;
    setNow(Date.now());
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [call]);
  if (call && isLive(status)) {
    const sec = Math.max(0, (now - call.startedAt) / 1000);
    const left = call.maxSeconds ? call.maxSeconds - sec : null;
    return (
      <span className={`meter is-live${left !== null && left < 60 ? " is-ending" : ""} ${className}`} aria-label={`Call time ${fmtClock(sec)}${call.charged ? `, ${callCredits(sec, call.creditsPerMin)} credits so far` : ""}`}>
        <span className="cao-timecode">{fmtClock(sec)}</span>
        {call.charged && <span>{callCredits(sec, call.creditsPerMin)} credits</span>}
        {left !== null && left < 60 && <span>{Math.max(0, Math.ceil(left))} s left</span>}
      </span>
    );
  }
  if (!rate.charged) return null;
  return <span className={`meter ${className}`}>{textRate < rate.creditsPerMin ? `${rate.creditsPerMin} credits a minute to talk, ${textRate} to type` : `${rate.creditsPerMin} credits a minute`}</span>;
}

/** When a call couldn't start for lack of credits: the way to fix it. */
export function TopUpLink({ className = "" }: { className?: string }) {
  const code = useStore((s) => s.directorErrorCode);
  if (code !== "NO_CREDITS") return null;
  return (
    <Link to="/credits" className={`btn btn-primary btn-small topup ${className}`}>
      Buy credits
    </Link>
  );
}

/** The Director's status as shown: drops back from speaking/thinking only after a short hold. */
export function useShownStatus(): DirectorStatus {
  const raw = useStore((s) => s.director);
  const [shown, setShown] = useState(raw);
  useEffect(() => {
    if (raw === shown) return;
    const settling = (shown === "speaking" || shown === "thinking") && raw === "listening";
    if (!settling) return setShown(raw);
    const t = window.setTimeout(() => setShown(raw), 450);
    return () => window.clearTimeout(t);
  }, [raw, shown]);
  return shown;
}

const WORD: Record<DirectorStatus, string> = { off: "Tap to talk", connecting: "Connecting", listening: "Listening", thinking: "Working", speaking: "Speaking", error: "Tap to retry" };

function useWord(status: DirectorStatus) {
  const doing = useStore((s) => s.directorDoing);
  const muted = useStore((s) => s.micMuted);
  const watching = useStore((s) => isActive(s.playback) && s.directorQuiet);
  const ended = useStore((s) => s.directorEnded);
  const code = useStore((s) => s.directorErrorCode);
  const typed = useStore((s) => s.directorTyped);
  if (status === "error" && limitWord(code)) return limitWord(code)!;
  if (status === "off" && ended === "pause") return "Paused";
  if (status === "off" && ended) return "Call ended";
  if (!isLive(status)) return WORD[status];
  if (watching) return "Quiet while it plays";
  if (status === "thinking") return doing ?? WORD.thinking;
  if (typed) return typedWord(status, typed) ?? WORD[status];
  if (muted && status === "listening") return "Muted";
  return WORD[status];
}

/** The latest line under the orb: what the Director (or you) last said, or what to do next. */
function useLine(status: DirectorStatus) {
  const last = useStore((s) => [...s.transcript].reverse().find((l) => !l.artifact && l.who !== "studio") ?? s.transcript.at(-1));
  const ended = useStore((s) => s.directorEnded);
  const muted = useStore((s) => s.micMuted);
  const error = useStore((s) => s.directorError);
  const playing = useStore((s) => isActive(s.playback));
  if (status === "error" && error) return { text: error, you: false };
  if (status === "off" && ended === "idle") return { text: "Ended after three quiet minutes. Tap to pick up where you left off.", you: false };
  // END-CALL: the Director hung up, or the call ended when they left the app
  const after = status === "off" ? hangupLine(ended, playing) : null;
  if (after) return { text: after, you: false };
  if (isLive(status) && muted && status === "listening") return { text: "Your Director can still talk. Type to answer.", you: false };
  if (last) return { text: last.text, you: last.who === "you" };
  return { text: status === "off" ? "Ask for anything on this page. Tap the light to talk." : "Go ahead.", you: false };
}

export function MuteButton({ small = false }: { small?: boolean }) {
  const status = useStore((s) => s.director);
  const muted = useStore((s) => s.micMuted);
  const text = useStore((s) => s.directorTyped);
  if (!isLive(status) || text) return null;
  return (
    <button
      type="button"
      className={`mute-btn${muted ? " is-on" : ""}${small ? " mute-btn-small" : ""}`}
      aria-pressed={muted}
      aria-label={muted ? "Unmute your mic" : "Mute your mic (the call stays open)"}
      title={muted ? "Unmute" : "Mute"}
      data-action="call.mute" onClick={() => set({ micMuted: !muted })}
    >
      <Icon name={muted ? "mic-off" : "mic"} size={small ? 18 : 20} />
    </button>
  );
}

/**
 * 2026-11-03 (James's report): pause a voice call. It ends (nothing billed while away) and the conversation carries
 * over, so the next tap picks up where it left off. Not for a typed chat, which costs nothing while it waits.
 */
export function PauseButton({ small = false }: { small?: boolean }) {
  const status = useStore((s) => s.director);
  const text = useStore((s) => s.directorTyped);
  if (!isLive(status) || text) return null;
  return (
    <button
      type="button"
      className={`mute-btn pause-btn${small ? " mute-btn-small" : ""}`}
      aria-label="Pause the call (pick up where you left off)"
      title="Pause the call"
      data-action="call.pause"
      onClick={() => pauseCall()}
    >
      <Icon name="pause" size={small ? 18 : 20} />
    </button>
  );
}

/**
 * Phones: a bar above the tabs. Desktop "panel": the head of the suite's side panel, with the chat under it.
 * v4: this is the call banner's slot too. While a call is on and the stage is minimised, CallBanner (src/stage) sits
 * on top of it with what's on stage and "Back to stage".
 */
export function DirectorBar({ variant = "bar" }: { variant?: "bar" | "panel" }) {
  const status = useShownStatus();
  const word = useWord(status);
  const line = useLine(status);
  const live = isLive(status);
  const typed = useStore((s) => s.directorTyped);
  const [chat, setChat] = useState(false);
  const muted = useStore((s) => s.micMuted);
  const count = useStore((s) => s.transcript.length);
  const seen = useRef(count);
  const unread = !chat && count > seen.current;
  useEffect(() => {
    if (chat) seen.current = count;
  }, [chat, count]);

  const orb = (
    <button data-action="call.talk" type="button" className="dbar-orb" onClick={() => toggleTalk(status)} aria-label={typed && live ? "Talk out loud instead" : live ? "End the call with your Director" : "Talk to your Director"} aria-pressed={live}>
      <DirectorMark status={status} />
    </button>
  );
  const text = (
    <div className="dbar-text" aria-live="polite">
      <p className={`dbar-word dbar-word-${status}`}>
        {word}
        <CallMeter className="dbar-meter" />
      </p>
      <p className={`dbar-line${line.you ? " is-you" : ""}`}>{line.text}</p>
    </div>
  );

  if (variant === "panel")
    return (
      <section className={`dpanel dpanel-${status}`} aria-label="Your Director">
        <CallBanner variant="panel" />
        <div className="dpanel-head">
          {orb}
          {text}
          <MuteButton small />
          <PauseButton small />
          <TopUpLink />
        </div>
        <div className="dpanel-chat">
          {/* v4 (V4-P2): "director.conversation", open until they shut it; typing stays outside the fold */}
          <Fold id="director.conversation" title="Conversation" count={count || undefined} summary={count ? undefined : "Nothing said yet"} defaultOpen className="dpanel-fold">
            <Transcript limit={8} compact />
          </Fold>
          <Composer placeholder="Type to your Director" />
        </div>
      </section>
    );

  return (
    <>
      <CallBanner />
      <div className={`dbar dbar-${status}`}>
        {orb}
        <button type="button" className="dbar-open" onClick={() => setChat(true)} aria-label="Open the conversation">
          {text}
        </button>
        <MuteButton />
        <PauseButton />
        <TopUpLink />
        <button type="button" className="dbar-chat" onClick={() => setChat(true)} aria-label="Open the conversation">
          <Icon name="chat" size={20} />
          {unread && <span className="dot" aria-hidden />}
        </button>
      </div>
      {chat && (
        <Sheet title="Your Director" onClose={() => setChat(false)} className="chat-sheet">
          <div className="sheet-body chat-body">
            <div className="chat-status">
              <span className="chat-orb">
                <DirectorMark status={status} />
              </span>
              <span>
                <strong>
                  {word} <CallMeter className="dbar-meter" />
                </strong>
                <span className="muted">{live && muted ? "Your mic is off. Type to answer." : live ? "The call is open. Talk or type." : "Type to start, or tap the light to talk."}</span>
              </span>
              <MuteButton small />
              <PauseButton small />
            </div>
            <Transcript limit={30} compact />
            <Composer placeholder="Type to your Director" />
          </div>
        </Sheet>
      )}
    </>
  );
}
